/**
 * Origin resolution for the two backends.
 *
 * In production the SPA is served by either `https://dex.lotosterminal.pro`
 * or `https://cex.lotosterminal.pro`. One of the two halves is therefore
 * reachable via `window.location.origin` without cross-origin cost; the
 * OTHER is always reached via its absolute URL.
 *
 * In dev (Vite) we use the proxy paths `/dex-api` and `/cex-api`.
 */

const PROD_DEX = 'https://dex.lotosterminal.pro'
const PROD_CEX = 'https://cex.lotosterminal.pro'

function isDev() {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return import.meta.env.DEV === true
  }
  return false
}

export function dexBase() {
  if (isDev()) return ''
  return PROD_DEX
}

export function cexBase() {
  if (isDev()) return ''
  return PROD_CEX
}

export function dexApi(path) {
  if (isDev()) {
    if (path.startsWith('/api/')) return `/dex-api${path.slice(4)}`
    return `/dex-api${path.startsWith('/') ? path : `/${path}`}`
  }
  return `${PROD_DEX}${path.startsWith('/') ? path : `/${path}`}`
}

/**
 * DEX URL for RUM (web-vitals). Override with `VITE_DEX_BASE_URL` when the dashboard
 * host differs from `PROD_DEX`. Same host as DEX → relative `/api/rum` (no extra DNS).
 */
export function dexRumUrl() {
  const fromEnv =
    typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_DEX_BASE_URL
  const envBase = fromEnv != null && String(fromEnv).trim() ? String(fromEnv).replace(/\/$/, '') : ''
  if (envBase) return `${envBase}/api/rum`
  if (typeof window !== 'undefined' && !isDev()) {
    let dexHost = 'dex.lotosterminal.pro'
    try {
      dexHost = new URL(PROD_DEX).hostname
    } catch {
      /* keep default */
    }
    if (window.location.hostname === dexHost) return '/api/rum'
  }
  return dexApi('/api/rum')
}

export function cexApi(path) {
  if (isDev()) {
    if (path.startsWith('/api/')) return `/cex-api${path.slice(4)}`
    return `/cex-api${path.startsWith('/') ? path : `/${path}`}`
  }
  return `${PROD_CEX}${path.startsWith('/') ? path : `/${path}`}`
}

/**
 * CEX RUM endpoint (web-vitals) when the SPA is served from the CEX host; mirrors DEX /api/rum.
 */
/**
 * Which audit log source the Logs page should emphasize.
 * - `dex` — only LTDashboard `/api/logs` (dex.lotosterminal.pro)
 * - `cex` — only CEX control-plane `/api/cex/audit-log` (cex.lotosterminal.pro)
 * - `both` — dev / unknown host: show both (stacked) for local testing
 */
export function logsHostMode() {
  if (typeof window === 'undefined') return 'dex'
  const h = String(window.location.hostname || '').toLowerCase()
  if (h === 'cex.lotosterminal.pro' || (h.length > 4 && h.startsWith('cex.'))) {
    return 'cex'
  }
  if (h === 'dex.lotosterminal.pro' || (h.length > 3 && h.startsWith('dex.'))) {
    return 'dex'
  }
  return 'both'
}

export function cexRumUrl() {
  if (isDev()) {
    return cexApi('/api/platform/rum')
  }
  if (typeof window !== 'undefined') {
    try {
      const cexHost = new URL(PROD_CEX).hostname
      if (window.location.hostname === cexHost) {
        return '/api/platform/rum'
      }
    } catch {
      /* fall through */
    }
  }
  return `${PROD_CEX}/api/platform/rum`
}

/**
 * CEX control-plane WebSocket (`/api/ws`). When the SPA is already on the CEX host, use
 * `window.location` so the URL matches nginx and the session; from dex.* the cookie still works
 * to `wss://cex.…/api/ws` (same parent domain by default in production).
 */
export function cexWebSocketUrl() {
  if (isDev()) {
    const protocol =
      typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'wss' : 'ws'
    return `${protocol}://${window.location.host}/cex-api/ws`
  }
  if (typeof window !== 'undefined') {
    let cexHost = 'cex.lotosterminal.pro'
    try {
      cexHost = new URL(PROD_CEX).hostname
    } catch {
      /* keep default */
    }
    if (window.location.hostname === cexHost) {
      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
      return `${protocol}://${window.location.host}/api/ws`
    }
  }
  return `${PROD_CEX.replace(/^http/, 'ws')}/api/ws`
}

/**
 * DEX-ресурсы (`/api/avatars/...` и т.д.) в ответе API часто относительные. На
 * cex. поддомене `src="/api/…"` идёт на CEX, а не на LT Dashboard — картинки
 * пустые. Собираем полный URL на DEX (в dev — `/dex-api/...` через Vite).
 */
export function absoluteDexUrl(pathOrUrl) {
  if (pathOrUrl == null || typeof pathOrUrl !== 'string') return pathOrUrl
  const t = pathOrUrl.trim()
  if (!t) return pathOrUrl
  if (t.startsWith('data:') || t.startsWith('blob:')) return pathOrUrl
  if (/^https?:\/\//i.test(t)) return pathOrUrl
  const path = t.startsWith('/') ? t : `/${t}`
  return dexApi(path)
}
